import React, { useState } from 'react';
import { Dialog, DialogContent, Button, Typography, Box, styled, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Star, AllInclusive, Group, Rocket, Lock, Close } from '@mui/icons-material';
import { fetchAuthSession } from '@aws-amplify/auth';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
}

const FeatureItem = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <Box display="flex" alignItems="center" mb={2}>
    <Box mr={2}>{icon}</Box>
    <Box>
      <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
    </Box>
  </Box>
);

const PlanOption = ({ title, price, isSelected, onClick }: { title: string; price: string; isSelected: boolean; onClick: () => void }) => (
  <StyledPlanOption
    onClick={onClick}
    isSelected={isSelected}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <Typography variant="h6">{title}</Typography>
    <Typography variant="body1">{price}</Typography>
    {isSelected && <CheckCircle sx={{ position: 'absolute', top: 10, right: 10, color: '#7B2CBF' }} />}
  </StyledPlanOption>
);

export default function UpgradeModal({ open, onClose }: UpgradeModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');

  const handleUpgrade = async () => {
    // Implement upgrade logic here
    let priceID;
    if (selectedPlan === 'monthly'){
      //priceID = 'price_1QMecWHWhHrfjpBarw6iIsqE'; test
      priceID = 'price_1RGCVbHWhHrfjpBaKYcxwNDM';
    } else {
      //priceID = 'price_1QMjwRHWhHrfjpBaZPuf48XL'; test
      priceID = 'price_1RGCVYHWhHrfjpBasCBkxlrs';
    }
  
    const apiUrl = `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/stripe/create-session`;
    const payload = {
      "priceId": priceID
    }
    try {
      const { tokens } = await fetchAuthSession();
      const authToken = tokens?.accessToken?.toString();

      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'x-api-key': authToken!, // Assuming token is available in fetchAuthSession
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      }).then((response) => response.json())
      .then((data) => {
          window.location.href = data.url; // Redirect to the Stripe Checkout session URL
      })
      .catch((error) => {
          console.error('Error:', error);
      })
    } catch (err) {
      console.error('Failed create stripe session: ', err);
      return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          background: '#FFFFFF',
        }
      }}
    >
      <DialogContent sx={{ position: 'relative', p: 4 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" fontWeight="bold" color="#7B2CBF" gutterBottom>
                Unlock the Full TimeCapsule Experience
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Elevate your memories with our Premium features
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" flexWrap="wrap">
              <Box width={{ xs: '100%', md: '48%' }} mb={4}>
                <Typography variant="h6" color="#7B2CBF" gutterBottom>
                  Premium Features
                </Typography>
                <FeatureItem
                  icon={<AllInclusive sx={{ color: '#7B2CBF' }} />}
                  title="Unlimited Capsules"
                  description="Create as many time capsules as you want"
                />
                <FeatureItem
                  icon={<Group sx={{ color: '#7B2CBF' }} />}
                  title="Extended Sharing"
                  description="Share your capsules with up to 100 friends"
                />
                <FeatureItem
                  icon={<Rocket sx={{ color: '#7B2CBF' }} />}
                  title="Priority Support"
                  description="Get help faster with our dedicated support team"
                />
                <FeatureItem
                  icon={<Lock sx={{ color: '#7B2CBF' }} />}
                  title="Advanced Privacy"
                  description="Extra security features to protect your memories"
                />
              </Box>

              <Box width={{ xs: '100%', md: '48%' }}>
                <Typography variant="h6" gutterBottom color="#7B2CBF">
                  Choose Your Plan
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <PlanOption
                    title="Monthly Plan"
                    price="$4.99/month"
                    isSelected={selectedPlan === 'monthly'}
                    onClick={() => setSelectedPlan('monthly')}
                  />
                  <PlanOption
                    title="Annual Plan"
                    price="$49.99/year (Save 20%)"
                    isSelected={selectedPlan === 'annual'}
                    onClick={() => setSelectedPlan('annual')}
                  />
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleUpgrade}
                  sx={{
                    mt: 3,
                    borderRadius: 8,
                    textTransform: 'none',
                    backgroundColor: '#7B2CBF',
                    '&:hover': {
                      backgroundColor: '#6A24A6',
                    },
                  }}
                  startIcon={<Star />}
                >
                  Upgrade Now
                </Button>
              </Box>
            </Box>

            <Box textAlign="center" mt={4}>
              <Typography variant="body2" color="text.secondary">
                Upgrade now and start creating unforgettable memories!
              </Typography>
            </Box>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

const StyledPlanOption = styled(motion.div)<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  backgroundColor: isSelected ? 'rgba(123, 44, 191, 0.1)' : theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2),
  border: `2px solid ${isSelected ? '#7B2CBF' : theme.palette.divider}`,
  cursor: 'pointer',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#7B2CBF',
  },
}));